import React from 'react';
import { FileText, Settings, Globe, Bot, Zap, Smartphone, Check } from 'lucide-react';
import { Category } from '../types';
import { t } from '../services/i18n';

interface CategoryData {
  score: number;
  totalIssues: number;
  criticalIssues: number;
}

interface CategoryScoreGridProps {
  categories: Record<Category | string, CategoryData>;
  onCategoryClick?: (category: Category | string) => void;
}

const getCategoryConfig = (category: Category | string) => {
  switch (category) {
    case Category.ON_PAGE:
      return {
        name: t('results.categoryOnPage') || 'On-Page SEO',
        icon: <FileText className="w-8 h-8 text-indigo-600" />,
        bgColor: 'bg-indigo-100',
        color: 'text-indigo-500',
      };
    case Category.TECHNICAL:
      return {
        name: t('results.categoryTechnical') || 'Technical SEO',
        icon: <Settings className="w-8 h-8 text-purple-600" />,
        bgColor: 'bg-purple-100',
        color: 'text-purple-500',
      };
    case 'Performance':
      return {
        name: 'Performance',
        icon: <Zap className="w-8 h-8 text-yellow-600" />,
        bgColor: 'bg-yellow-100',
        color: 'text-yellow-500',
      };
    case 'Mobile':
      return {
        name: 'Mobile',
        icon: <Smartphone className="w-8 h-8 text-blue-600" />,
        bgColor: 'bg-blue-100',
        color: 'text-blue-500',
      };
    default:
      return {
        name: category,
        icon: <FileText className="w-8 h-8 text-slate-600" />,
        bgColor: 'bg-slate-100',
        color: 'text-slate-500',
      };
  }
};

export const CategoryScoreGrid: React.FC<CategoryScoreGridProps> = ({ categories, onCategoryClick }) => {
  const categoryEntries = Object.entries(categories) as [Category | string, CategoryData][];
  const circumference = 2 * Math.PI * 40; // radius = 40

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12 px-6">
      {categoryEntries.map(([category, data], idx) => {
        const config = getCategoryConfig(category);
        const offset = circumference - (circumference * data.score / 100);

        // Dark mode color adjustments
        const darkColors: Record<string, { bg: string; color: string; iconColor: string }> = {
          [Category.ON_PAGE]: { bg: 'bg-indigo-500/20', color: 'text-indigo-400', iconColor: 'text-indigo-400' },
          [Category.TECHNICAL]: { bg: 'bg-purple-500/20', color: 'text-purple-400', iconColor: 'text-purple-400' },
          'Performance': { bg: 'bg-yellow-500/20', color: 'text-yellow-400', iconColor: 'text-yellow-400' },
          'Mobile': { bg: 'bg-blue-500/20', color: 'text-blue-400', iconColor: 'text-blue-400' },
        };
        const darkColor = darkColors[category] || { bg: 'bg-slate-500/20', color: 'text-slate-400', iconColor: 'text-slate-400' };

        return (
          <div
            key={category}
            onClick={() => onCategoryClick?.(category)}
            className="bg-slate-800 rounded-xl p-4 shadow-lg border border-slate-700 hover:border-slate-600 transition-all transform hover:-translate-y-1 cursor-pointer"
            style={{
              animation: `slideUp 0.5s ease-out ${idx * 0.1}s both`
            }}
          >
            {/* Icon */}
            <div className="flex justify-center mb-3">
              <div className={`p-3 rounded-lg ${darkColor.bg}`}>
                <div className={darkColor.iconColor}>
                  {config.icon}
                </div>
              </div>
            </div>

            {/* Score Ring - Smaller */}
            <div className="flex justify-center mb-2">
              <div className="relative w-20 h-20">
                <svg className="w-20 h-20 transform -rotate-90">
                  <circle
                    cx="40"
                    cy="40"
                    r="32"
                    stroke="currentColor"
                    strokeWidth="5"
                    fill="none"
                    className="text-slate-700"
                  />
                  <circle
                    cx="40"
                    cy="40"
                    r="32"
                    stroke="currentColor"
                    strokeWidth="5"
                    fill="none"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    className={`transition-all duration-1000 ease-out ${darkColor.color}`}
                    strokeLinecap="round"
                    style={{
                      strokeDashoffset: offset,
                      transition: 'stroke-dashoffset 1s ease-out'
                    }}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xl font-black text-white">{data.score}</span>
                </div>
              </div>
            </div>

            {/* Category Name */}
            <h4 className="text-center font-bold text-white mb-2 text-xs">{config.name}</h4>

            {/* Issues Count */}
            <div className="flex items-center justify-center gap-1 text-[10px] flex-wrap">
              {data.criticalIssues > 0 && (
                <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full font-semibold">
                  {data.criticalIssues} critical
                </span>
              )}
              {data.totalIssues > 0 && (
                <span className="px-2 py-0.5 bg-slate-700 text-slate-300 rounded-full font-semibold">
                  {data.totalIssues} total
                </span>
              )}
              {data.totalIssues === 0 && (
                <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-full font-semibold flex items-center gap-1">
                  <Check className="w-3 h-3" /> Perfect
                </span>
              )}
            </div>
          </div>
        );
      })}

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

